import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { defectService, Defect } from '../services/defectService';
import { groupService, Group } from '../services/groupService';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import StatsBar from '../components/StatsBar';
import FilterBar from '../components/FilterBar';
import DefectsTable from '../components/DefectsTable';
import Pagination from '../components/Pagination';
import '../styles/Dashboard.css';

const TestPage: React.FC = () => {
  const { user } = useAuth();
  const [defects, setDefects] = useState<Defect[]>([]);
  const [filteredDefects, setFilteredDefects] = useState<Defect[]>([]);
  const [parentGroups, setParentGroups] = useState<Group[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Pagination state (backend-driven)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Validation stats
  const [totalValidatedTrue, setTotalValidatedTrue] = useState(0);
  const [totalValidatedFalse, setTotalValidatedFalse] = useState(0);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedParentGroup, setSelectedParentGroup] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [groupByDescription, setGroupByDescription] = useState(false);
  
  // Sorting
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const [defectsRes, parentGroupsRes, groupsRes] = await Promise.all([
        defectService.getAll({
          page: currentPage,
          limit: groupByDescription ? 200 : pageSize, // Gruplama aktifken daha fazla veri çek
          sortBy: groupByDescription ? 'description' : 'startedAtDate', // Gruplama aktifken açıklamaya göre sırala
          sortOrder: groupByDescription ? 'asc' : 'desc',
          ...(selectedParentGroup && { parentControlItemGroupName: selectedParentGroup }),
          ...(selectedGroup && { controlItemGroupName: selectedGroup }),
          ...(selectedDate && { startedAtDate: selectedDate }),
          ...(searchTerm && { description: searchTerm }),
        }),
        groupService.getParentGroups(),
        groupService.getGroups(),
      ]);

      if (defectsRes.success) {
        setDefects(defectsRes.data);
        
        if (defectsRes.pagination) {
          setTotalRecords(defectsRes.pagination.totalRecords);
          setTotalPages(defectsRes.pagination.totalPages);
        }
        
        if (defectsRes.validationStats) {
          setTotalValidatedTrue(defectsRes.validationStats.totalValidatedTrue);
          setTotalValidatedFalse(defectsRes.validationStats.totalValidatedFalse);
        }
      }

      if (parentGroupsRes.success) {
        setParentGroups(parentGroupsRes.data);
      }

      if (groupsRes.success) {
        setGroups(groupsRes.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Veriler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, selectedParentGroup, selectedGroup, selectedDate, searchTerm, groupByDescription]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Client-side filtreleme artık gerekli değil, backend'de yapılıyor
  // Ancak sıralama için filteredDefects'i defects'e eşitliyoruz
  useEffect(() => {
    setFilteredDefects(defects);
  }, [defects]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Arama terimi değiştiğinde ilk sayfaya dön
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleValidationChange = async (defectId: string, newValidation: boolean) => {
    try {
      const response = await defectService.updateValidation(defectId, newValidation);
      
      if (response.success) {
        // Doğrulama yapıldığında veriyi yeniden yükle (grup ve üst grup alanlarının güncellenmesi için)
        await loadData();
        
        console.log(`✅ Doğrulama güncellendi: ${defectId} → ${newValidation}`);
      }
    } catch (error: any) {
      console.error('❌ Doğrulama güncellenirken hata:', error);
      alert('Doğrulama güncellenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleBulkValidationChange = async (description: string, validation: boolean) => {
    if (!window.confirm(
      `Bu açıklamaya sahip tüm kayıtları ${validation ? 'onaylamak' : 'reddetmek'} istediğinizden emin misiniz?`
    )) {
      return;
    }

    try {
      const response = await defectService.bulkValidateByDescription(description, validation);
      
      if (response.success) {
        alert(`${response.updatedCount} kayıt başarıyla güncellendi`);
        // Veriyi yeniden yükle
        await loadData();
        console.log(`✅ Toplu doğrulama: "${description.substring(0, 50)}..." → ${response.updatedCount} kayıt güncellendi`);
      }
    } catch (error: any) {
      console.error('❌ Toplu doğrulama güncellenirken hata:', error);
      alert('Toplu doğrulama güncellenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSuggestionsUpdate = async (defectId: string, suggestions: {
    generatedGroup?: string;
    generatedParentGroup?: string;
    generatedTag?: string;
  }) => {
    try {
      const response = await defectService.updateSuggestions(defectId, suggestions);
      
      if (response.success) {
        // UI'ı güncelle
        setDefects(prevDefects => 
          prevDefects.map(d => 
            (d._id || d.id) === defectId 
              ? { 
                  ...d, 
                  generatedGroup: suggestions.generatedGroup !== undefined ? suggestions.generatedGroup : (d as any).generatedGroup,
                  generatedParentGroup: suggestions.generatedParentGroup !== undefined ? suggestions.generatedParentGroup : (d as any).generatedParentGroup,
                  generatedTag: suggestions.generatedTag !== undefined ? suggestions.generatedTag : (d as any).generatedTag,
                }
              : d
          )
        );
        
        console.log(`✅ AI önerileri güncellendi: ${defectId}`);
      }
    } catch (error: any) {
      console.error('❌ Öneriler güncellenirken hata:', error);
      alert('Öneriler güncellenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleBulkSuggestionsUpdate = async (description: string, suggestions: {
    generatedGroup?: string;
    generatedParentGroup?: string;
    generatedTag?: string;
  }) => {
    try {
      const response = await defectService.bulkUpdateSuggestions(description, suggestions);
      
      if (response.success) {
        // Veriyi yeniden yükle
        await loadData();
        alert(`${response.updatedCount} kayıt başarıyla güncellendi`);
        console.log(`✅ Toplu AI önerileri güncellendi: "${description.substring(0, 50)}..." → ${response.updatedCount} kayıt`);
      }
    } catch (error: any) {
      console.error('❌ Toplu öneriler güncellenirken hata:', error);
      alert('Toplu öneriler güncellenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (defectId: string) => {
    if (!window.confirm('Bu hatayı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await defectService.delete(defectId);
      
      if (response.success) {
        // Silinen kaydın validation durumunu bul
        const deletedDefect = defects.find(d => (d._id || d.id) === defectId);
        
        // UI'dan kaldır
        setDefects(prevDefects => prevDefects.filter(d => (d._id || d.id) !== defectId));
        
        // Toplam sayacı azalt
        setTotalRecords(prev => prev - 1);
        
        // Validation stats güncelle
        if (deletedDefect?.validation === true) {
          setTotalValidatedTrue(prev => prev - 1);
        } else if (deletedDefect?.validation === false) {
          setTotalValidatedFalse(prev => prev - 1);
        }
        
        console.log(`🗑️ Hata silindi: ${defectId}`);
        alert('Hata başarıyla silindi');
      }
    } catch (error: any) {
      console.error('❌ Hata silinirken hata:', error);
      if (error.response?.status === 403) {
        alert('Bu işlem için admin yetkisi gereklidir');
      } else {
        alert('Hata silinirken bir hata oluştu: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const sortedDefects = useMemo(() => {
    if (!sortColumn) return filteredDefects;

    return [...filteredDefects].sort((a: any, b: any) => {
      const aVal = a[sortColumn] || '';
      const bVal = b[sortColumn] || '';

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [filteredDefects, sortColumn, sortDirection]);

  // Backend pagination kullanıyoruz - paginatedDefects = sortedDefects
  const paginatedDefects = sortedDefects;

  const exportToCSV = () => {
    const headers = [
      'Açıklama', 
      'Grup', 
      'Üst Grup', 
      'Önerilen Grup', 
      'Önerilen Üst Grup',
      'Önerilen Etiket', 
      'Önerilen Çözüm', 
      'Doğrulama'
    ];
    const rows = filteredDefects.map((defect: any) => [
      defect.description || '',
      defect.controlItemGroupName || '',
      defect.parentControlItemGroupName || '',
      defect.generatedGroup || '',
      defect.generatedParentGroup || '',
      defect.generatedTag || '',
      defect.generatedSolution || '',
      defect.validation ? 'Evet' : 'Hayır',
    ]);

    let csvContent = headers.join(',') + '\n';
    rows.forEach((row) => {
      csvContent += row.map((cell) => `"${cell}"`).join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `qualisu-hatalar-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="container">
      <Navigation />

      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '20px'
      }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', margin: 0 }}>📋 Hatalar</h1>
          <p style={{ color: '#666', marginTop: '5px', margin: 0 }}>
            Tüm hata kayıtları
          </p>
        </div>
        <div style={{ 
          flexShrink: 0,
          transform: 'scale(0.85)',
          transformOrigin: 'right center'
        }}>
          <StatsBar 
            defects={filteredDefects} 
            totalRecords={totalRecords}
            totalValidatedTrue={totalValidatedTrue}
            totalValidatedFalse={totalValidatedFalse}
          />
        </div>
      </div>

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        selectedParentGroup={selectedParentGroup}
        onParentGroupChange={setSelectedParentGroup}
        selectedGroup={selectedGroup}
        onGroupChange={setSelectedGroup}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        parentGroups={parentGroups}
        groups={groups}
        onRefresh={loadData}
        onExport={exportToCSV}
        groupByDescription={groupByDescription}
        onGroupByDescriptionChange={setGroupByDescription}
      />

      {isLoading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Hata verileri yükleniyor... (Sayfa {currentPage}/{totalPages})</p>
        </div>
      )}

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button className="close-btn" onClick={() => setError('')}>
            ✕
          </button>
        </div>
      )}

      {!isLoading && !error && (
        <>
          <DefectsTable
            defects={paginatedDefects}
            onSort={handleSort}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onValidationChange={handleValidationChange}
            onBulkValidationChange={handleBulkValidationChange}
            onSuggestionsUpdate={handleSuggestionsUpdate}
            onBulkSuggestionsUpdate={handleBulkSuggestionsUpdate}
            onDelete={handleDelete}
            groups={groups}
            parentGroups={parentGroups.map(g => g.name)}
            userRole={user?.role}
            groupByDescription={groupByDescription}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalRecords}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setCurrentPage(1);
            }}
          />

          <footer>
            <p>© 2025 Qualisu Paneli | Toplam Kayıt: {totalRecords.toLocaleString()} | Gösterilen: {filteredDefects.length}</p>
          </footer>
        </>
      )}
    </div>
  );
};

export default TestPage;

